import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const LETTERS = ['A', 'B', 'C', 'D', 'E']
const NUMBERS = ['1', '2', '3', '4']

interface RegistrationMarkProps {
    className?: string
}

/**
 * A crosshair registration mark: a small circle crossed by two hairlines, centred on its anchor.
 */
const RegistrationMark: FC<RegistrationMarkProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 18 18' className={cn('pointer-events-none absolute size-[18px]', className)} aria-hidden='true'>
            <circle cx={9} cy={9} r={4} fill='none' stroke='#86B6F0' strokeWidth={1} />
            <line x1={9} y1={0} x2={9} y2={18} stroke='#86B6F0' strokeWidth={1} />
            <line x1={0} y1={9} x2={18} y2={9} stroke='#86B6F0' strokeWidth={1} />
        </svg>
    )
}

/**
 * The drawing frame around the footer sheet: registration marks on the four corners, grid letters
 * A to E along the top edge and numbers 1 to 4 down the left edge (from 900px).
 */
export const SheetFrame: FC = (): ReactNode => {
    return (
        <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
            <RegistrationMark className='-left-[9px] -top-[9px]' />
            <RegistrationMark className='-right-[9px] -top-[9px]' />
            <RegistrationMark className='-bottom-[9px] -left-[9px]' />
            <RegistrationMark className='-bottom-[9px] -right-[9px]' />

            <div className='absolute inset-x-0 left-8 top-0 hidden h-8 desk:block'>
                {LETTERS.map((letter, index) => (
                    <span key={letter} className='absolute top-0 flex h-full -translate-x-1/2 flex-col items-center justify-between' style={{ left: `${(index + 0.5) * 20}%` }}>
                        <span className='block h-1.5 w-px bg-hairline' />
                        <span className='font-mono text-[10px] leading-none tracking-[0.1em] text-dim'>
                            {letter}
                        </span>
                        <span className='block h-1.5 w-px bg-hairline' />
                    </span>
                ))}

                {[1, 2, 3, 4].map(index => (
                    <span key={index} className='absolute top-0 block h-2.5 w-px bg-hairline' style={{ left: `${index * 20}%` }} />
                ))}
            </div>

            <div className='absolute bottom-0 left-0 top-8 hidden w-8 desk:block'>
                {NUMBERS.map((number, index) => (
                    <span key={number} className='absolute left-0 flex w-full -translate-y-1/2 items-center justify-between' style={{ top: `${(index + 0.5) * 25}%` }}>
                        <span className='block h-px w-1.5 bg-hairline' />
                        <span className='font-mono text-[10px] leading-none tracking-[0.1em] text-dim'>
                            {number}
                        </span>
                        <span className='block h-px w-1.5 bg-hairline' />
                    </span>
                ))}

                {[1, 2, 3].map(index => (
                    <span key={index} className='absolute left-0 block h-px w-2.5 bg-hairline' style={{ top: `${index * 25}%` }} />
                ))}
            </div>
        </div>
    )
}
